import React, { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import { useCart } from '../../context/CartContext';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { ShoppingCart, Sun, Moon, Globe, Search } from 'lucide-react';
import PremiumSearch from '../search/PremiumSearch';

const Header = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const { getCartCount } = useCart();
  const cartCount = getCartCount();
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);

  // Helper function to determine if link is active
  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  // Smooth scroll to about section
  const scrollToAbout = (e) => {
    e.preventDefault();
    
    // If not on home page, navigate to home first
    if (location.pathname !== '/') {
      window.location.href = '/#about';
      return;
    }
    
    const aboutSection = document.getElementById('about');
    if (aboutSection) {
      aboutSection.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        {/* Logo - Clickeable with hover effect */}
        <button
          onClick={scrollToAbout}
          className="flex items-center space-x-2 hover:opacity-70 transition-opacity cursor-pointer group"
        >
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight whitespace-nowrap">
            NAGOC <span className="text-muted-foreground font-light">GROUP<span className="hidden sm:inline">, S.R.L</span></span>
            <span className="ml-2 text-xs text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity">
              ↓
            </span>
          </h1>
        </button>

        {/* Navigation */}
        <nav className="hidden md:flex items-center space-x-6 text-sm font-medium">
          <Link 
            to="/" 
            className={`transition-colors hover:text-foreground/80 ${
              isActive('/') 
                ? 'text-foreground' 
                : 'text-foreground/60'
            }`}
          >
            {t('home')}
          </Link>
          <Link 
            to="/catalog" 
            className={`transition-colors hover:text-foreground/80 ${
              isActive('/catalog') 
                ? 'text-foreground' 
                : 'text-foreground/60'
            }`}
          >
            {t('catalog')}
          </Link>
          <Link 
            to="/featured" 
            className={`transition-colors hover:text-foreground/80 ${
              isActive('/featured') 
                ? 'text-foreground' 
                : 'text-foreground/60'
            }`}
          >
            {t('featured')}
          </Link>
          <Link 
            to="/contact" 
            className={`transition-colors hover:text-foreground/80 ${
              isActive('/contact') 
                ? 'text-foreground' 
                : 'text-foreground/60'
            }`}
          >
            {t('contact')}
          </Link>
        </nav>

        {/* Actions */}
        <div className="flex items-center space-x-2">
          {/* Search */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSearchOpen(true)}
            className="w-9 h-9"
          >
            <Search className="h-4 w-4" />
            <span className="sr-only">Search</span>
          </Button>

          {/* Language Toggle */}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleLanguage}
            className="w-9 h-9"
          >
            <Globe className="h-4 w-4" />
            <span className="sr-only">Toggle language</span>
            <span className="ml-1 text-xs">{language.toUpperCase()}</span>
          </Button>

          {/* Theme Toggle */}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="w-9 h-9"
          >
            {theme === 'light' ? (
              <Moon className="h-4 w-4" />
            ) : (
              <Sun className="h-4 w-4" />
            )}
            <span className="sr-only">Toggle theme</span>
          </Button>

          {/* Cart */}
          <Link to="/cart">
            <Button variant="ghost" size="icon" className="relative w-9 h-9">
              <ShoppingCart className="h-4 w-4" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Button>
          </Link>
        </div>
      </div>

      {/* Premium Search Modal */}
      <PremiumSearch isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
};

export default Header;
